import React, { useState, useEffect, useRef } from "react";
import {
  Send,
  Bot,
  Rocket,
  Shield,
  Flame,
  Globe,
  Sparkles,
  Zap,
  MessageSquare,
  Crown,
  Terminal,
  Cpu,
  Layers,
  Radio,
  Compass,
  Users,
  HeartHandshake,
  Target,
  RotateCcw,
  Upload,
  Image as ImageIcon,
  Link as LinkIcon,
  Sliders,
  Palette,
  Monitor,
  Smartphone,
  Info,
  AlertCircle,
  Save,
  CheckCircle2,
  RefreshCw,
  X,
  Type,
  LayoutTemplate,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  Maximize2,
  Minimize2,
  ExternalLink,
} from "lucide-react";

export interface GroupBrandSetting {
  group: string;
  systemName: string;
  mobileName: string;
  slogan?: string;
  browserTitle?: string;
  logoType: "preset" | "image";
  logoIcon: string;
  logoImageUrl?: string;
  logoBg: string;
  logoBorder: string;
  logoColor: string;
  logoRadius: string;
  // Favicon settings
  faviconType?: "sync" | "preset" | "custom";
  faviconIcon?: string;
  faviconUrl?: string;
  faviconBg?: string;
  faviconShape?: "rounded" | "circle" | "square";
  updatedAt?: number;
  updatedBy?: string;
  isDefault?: boolean;
}

export const DEFAULT_BRAND_SETTING: GroupBrandSetting = {
  group: "默认分组",
  systemName: "Telegram 营销自动化管理中心",
  mobileName: "Telegram 营销系统",
  slogan: "多账号矩阵·自动化获客中枢",
  browserTitle: "Telegram 营销自动化管理中心",
  logoType: "preset",
  logoIcon: "Send",
  logoImageUrl: "",
  logoBg: "bg-sky-500/10",
  logoBorder: "border-sky-500/20",
  logoColor: "text-sky-400",
  logoRadius: "rounded-lg",
  faviconType: "sync",
  faviconIcon: "Send",
  faviconUrl: "",
  faviconBg: "#0284c7",
  faviconShape: "rounded",
  isDefault: true,
};

// Preset vector icons mapping
export const PRESET_ICONS = [
  { key: "Send", label: "纸飞机 (Telegram)", icon: Send },
  { key: "Bot", label: "智能机器人", icon: Bot },
  { key: "Rocket", label: "加速火箭", icon: Rocket },
  { key: "Shield", label: "安全护盾", icon: Shield },
  { key: "Flame", label: "极速爆款", icon: Flame },
  { key: "Globe", label: "全球拓客", icon: Globe },
  { key: "Sparkles", label: "AI 智能星光", icon: Sparkles },
  { key: "Zap", label: "闪电快发", icon: Zap },
  { key: "MessageSquare", label: "即时会话", icon: MessageSquare },
  { key: "Crown", label: "尊享皇冠", icon: Crown },
  { key: "Terminal", label: "矩阵终端", icon: Terminal },
  { key: "Cpu", label: "运算中枢", icon: Cpu },
  { key: "Layers", label: "分层矩阵", icon: Layers },
  { key: "Radio", label: "无线广播", icon: Radio },
  { key: "Compass", label: "雷达罗盘", icon: Compass },
  { key: "Users", label: "社群矩阵", icon: Users },
  { key: "HeartHandshake", label: "商务合作", icon: HeartHandshake },
  { key: "Target", label: "精准定位", icon: Target },
];

export const PRESET_COLOR_THEMES = [
  {
    id: "sky",
    name: "天空蓝 (经典 Telegram)",
    bg: "bg-sky-500/10",
    border: "border-sky-500/20",
    color: "text-sky-400",
    ring: "ring-sky-400",
    previewBg: "bg-sky-500",
  },
  {
    id: "indigo",
    name: "深邃靛蓝 (专业商务)",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
    color: "text-indigo-400",
    ring: "ring-indigo-400",
    previewBg: "bg-indigo-500",
  },
  {
    id: "emerald",
    name: "极光翡翠 (高效获客)",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    color: "text-emerald-400",
    ring: "ring-emerald-400",
    previewBg: "bg-emerald-500",
  },
  {
    id: "amber",
    name: "活力琥珀 (爆单热潮)",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    color: "text-amber-400",
    ring: "ring-amber-400",
    previewBg: "bg-amber-500",
  },
  {
    id: "rose",
    name: "热烈玫红 (高亮焦点)",
    bg: "bg-rose-500/10",
    border: "border-rose-500/20",
    color: "text-rose-400",
    ring: "ring-rose-400",
    previewBg: "bg-rose-500",
  },
  {
    id: "purple",
    name: "暗夜星紫 (尊贵矩阵)",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    color: "text-purple-400",
    ring: "ring-purple-400",
    previewBg: "bg-purple-500",
  },
  {
    id: "cyan",
    name: "赛博青蓝 (数字矩阵)",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500/20",
    color: "text-cyan-400",
    ring: "ring-cyan-400",
    previewBg: "bg-cyan-500",
  },
];

export const PRESET_RADIUS_OPTIONS = [
  { id: "rounded-lg", name: "标准圆角 (8px)", class: "rounded-lg" },
  { id: "rounded-xl", name: "微弧圆角 (12px)", class: "rounded-xl" },
  { id: "rounded-2xl", name: "大圆角 (16px)", class: "rounded-2xl" },
  { id: "rounded-full", name: "圆形徽标 (Full)", class: "rounded-full" },
];

// Favicon Presets
export const PRESET_FAVICON_ICONS = [
  { key: "Send", label: "纸飞机 (Telegram)", icon: Send },
  { key: "Bot", label: "智能机器人", icon: Bot },
  { key: "Rocket", label: "加速火箭", icon: Rocket },
  { key: "Shield", label: "安全护盾", icon: Shield },
  { key: "Flame", label: "极速爆款", icon: Flame },
  { key: "Globe", label: "全球拓客", icon: Globe },
  { key: "Sparkles", label: "AI 智能星光", icon: Sparkles },
  { key: "Zap", label: "闪电快发", icon: Zap },
  { key: "Crown", label: "尊享皇冠", icon: Crown },
  { key: "Terminal", label: "矩阵终端", icon: Terminal },
  { key: "MessageSquare", label: "即时会话", icon: MessageSquare },
  { key: "Target", label: "精准定位", icon: Target },
];

export const PRESET_FAVICON_COLORS = [
  { id: "sky", name: "天空蓝", hex: "#0284c7", bgClass: "bg-sky-600" },
  { id: "indigo", name: "深邃靛蓝", hex: "#4f46e5", bgClass: "bg-indigo-600" },
  { id: "emerald", name: "极光翡翠", hex: "#059669", bgClass: "bg-emerald-600" },
  { id: "amber", name: "活力琥珀", hex: "#d97706", bgClass: "bg-amber-600" },
  { id: "rose", name: "热烈玫红", hex: "#e11d48", bgClass: "bg-rose-600" },
  { id: "purple", name: "暗夜星紫", hex: "#7c3aed", bgClass: "bg-purple-600" },
  { id: "cyan", name: "赛博科技青", hex: "#0891b2", bgClass: "bg-cyan-600" },
  { id: "dark", name: "质感深黑", hex: "#0f172a", bgClass: "bg-slate-900" },
];

export const PRESET_FAVICON_SHAPES = [
  { id: "rounded", name: "平滑微圆角 (推荐)", desc: "现代高质感应用标准", rx: 16 },
  { id: "circle", name: "正圆形徽章", desc: "经典圆形标识", rx: 32 },
  { id: "square", name: "标准方块", desc: "全幅无倒角硬朗风格", rx: 4 },
];

// SVG vector paths for crisp Favicon rendering at 32x32 / 64x64
const FAVICON_SVG_PATHS: Record<string, string> = {
  Send: "M52 12 12 32l16 6 6 16 7-10 11 9 4-41-4 0z",
  Bot: "M20 18h24v22H20zm12-8v8m-16 12h-4m36 0h4m-28 6h4m12 0h4",
  Rocket: "m32 6-8 12c-6 9-6 17-6 17l6 6s8 0 17-6l12-8zm-14 36-6 16 16-6",
  Shield: "M32 6s18 4 18 18c0 18-18 28-18 28S14 42 14 24C14 10 32 6 32 6z",
  Globe: "M32 6a26 26 0 1 0 0 52 26 26 0 0 0 0-52zm0 0c7 8 10 17 10 26s-3 18-10 26m0-52c-7 8-10 17-10 26s3 18 10 26M6 32h52",
  Sparkles: "m32 6 5 15 15 5-15 5-5 15-5-15-15-5 15-5 5-15z",
  Zap: "M36 6 14 36h16l-4 22 24-32H34l2-20z",
  Crown: "m12 46 6-28 14 14 14-14 6 28zm0 0h40",
  Terminal: "m14 18 10 8-10 8m14 8h16",
  MessageSquare: "M14 12h36a4 4 0 0 1 4 4v24a4 4 0 0 1-4 4H24l-12 10V16a4 4 0 0 1 4-4z",
  Target: "M32 10a22 22 0 1 0 0 44 22 22 0 0 0 0-44zm0 10a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm0 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z",
  Flame: "M32 8c4 8 16 16 16 28a16 16 0 0 1-32 0c0-10 8-18 12-24 1 6 4 8 4 8z",
};

/**
 * Generate a dynamic, high-resolution SVG Favicon Data URI
 */
export function generateFaviconDataUri(brand: Partial<GroupBrandSetting>): string {
  // 1. Custom URL or Base64 Image
  if (brand.faviconType === "custom" && brand.faviconUrl && brand.faviconUrl.trim()) {
    return brand.faviconUrl.trim();
  }

  // 2. Sync with system Logo: if logo is custom image, use that image
  if (brand.faviconType === "sync" && brand.logoType === "image" && brand.logoImageUrl && brand.logoImageUrl.trim()) {
    return brand.logoImageUrl.trim();
  }

  // 3. Determine icon key
  let iconKey = "Send";
  if (brand.faviconType === "preset" && brand.faviconIcon) {
    iconKey = brand.faviconIcon;
  } else if (brand.faviconType === "sync" && brand.logoIcon) {
    iconKey = brand.logoIcon;
  }

  const pathD = FAVICON_SVG_PATHS[iconKey] || FAVICON_SVG_PATHS["Send"];
  const bgColor = brand.faviconBg || "#0284c7";
  const shape = brand.faviconShape || "rounded";

  let bgElement = `<rect x="2" y="2" width="60" height="60" rx="16" fill="${bgColor}"/>`;
  if (shape === "circle") {
    bgElement = `<circle cx="32" cy="32" r="30" fill="${bgColor}"/>`;
  } else if (shape === "square") {
    bgElement = `<rect x="2" y="2" width="60" height="60" rx="4" fill="${bgColor}"/>`;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">${bgElement}<path d="${pathD}" fill="#ffffff" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Apply the generated favicon directly to the document <head>
 */
export function applyFaviconToDocument(brand: Partial<GroupBrandSetting>) {
  try {
    const faviconUri = generateFaviconDataUri(brand);
    if (!faviconUri) return;

    let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.type = faviconUri.startsWith("data:image/svg") ? "image/svg+xml" : "image/x-icon";
    link.href = faviconUri;
  } catch (err) {
    console.warn("[Favicon] Could not apply favicon:", err);
  }
}

export function BrandIconRenderer({
  iconName,
  className = "w-4 h-4",
}: {
  iconName: string;
  className?: string;
}) {
  const found = PRESET_ICONS.find((i) => i.key === iconName);
  const IconComp = found ? found.icon : Send;
  return <IconComp className={className} />;
}

// Reusable Collapsible Panel Component
const CollapsiblePanel: React.FC<{
  id: string;
  title: string;
  subtitle: string;
  icon: any;
  iconColorClass?: string;
  badge?: React.ReactNode;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
}> = ({
  id,
  title,
  subtitle,
  icon: Icon,
  iconColorClass = "text-sky-400",
  badge,
  isCollapsed,
  onToggle,
  children,
  headerAction,
}) => {
  return (
    <div
      id={id}
      className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden transition-all duration-200"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-850/60 cursor-pointer select-none transition-colors border-b focus:outline-none focus:ring-1 focus:ring-sky-500/30 ${
          isCollapsed ? "border-transparent" : "border-slate-800/80 bg-slate-900/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-xl bg-slate-950 border border-slate-800 shrink-0 ${iconColorClass}`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-white tracking-tight">{title}</h2>
              {badge}
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div
          className="flex items-center gap-2.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {headerAction}
          <button
            type="button"
            onClick={onToggle}
            className="p-1.5 sm:px-2.5 sm:py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 transition cursor-pointer flex items-center gap-1.5 text-xs font-mono"
            aria-label={isCollapsed ? "展开面板" : "折叠面板"}
          >
            <span className="text-[10px] text-slate-400 hidden sm:inline">
              {isCollapsed ? "展开" : "折叠"}
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                isCollapsed ? "-rotate-90 text-slate-500" : "rotate-0 text-sky-400"
              }`}
            />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-4 sm:p-6 space-y-5 animate-in fade-in duration-150">
          {children}
        </div>
      )}
    </div>
  );
};

interface SystemSettingsViewProps {
  currentUser: any;
  currentBrand: GroupBrandSetting;
  onBrandUpdated: (brand: GroupBrandSetting) => void;
  availableGroups?: string[];
}

export const SystemSettingsView: React.FC<SystemSettingsViewProps> = ({
  currentUser,
  currentBrand,
  onBrandUpdated,
  availableGroups = [],
}) => {
  const isAdmin = currentUser?.role === "admin";
  const userGroup = (currentUser?.group || "默认分组").trim();

  // Selected group to edit (admin can choose any group; manager/user is locked to userGroup)
  const [selectedGroup, setSelectedGroup] = useState<string>(
    isAdmin ? userGroup || "默认分组" : userGroup
  );

  // Group list state (for super admin)
  const [groupOptions, setGroupOptions] = useState<string[]>(() => {
    const s = new Set<string>(["默认分组"]);
    if (userGroup) s.add(userGroup);
    availableGroups.forEach((g) => g && s.add(g));
    return Array.from(s);
  });

  const [isCustomGroupInput, setIsCustomGroupInput] = useState(false);
  const [customGroupName, setCustomGroupName] = useState("");

  // Current editing form state
  const [formData, setFormData] = useState<GroupBrandSetting>({
    ...DEFAULT_BRAND_SETTING,
    group: selectedGroup,
  });

  // Collapsed state for each panel
  const [collapsedPanels, setCollapsedPanels] = useState<Record<string, boolean>>({
    groupInfo: false,
    namesConfig: false,
    logoConfig: false,
    faviconConfig: false,
    livePreview: false,
  });

  const togglePanel = (panelId: string) => {
    setCollapsedPanels((prev) => ({
      ...prev,
      [panelId]: !prev[panelId],
    }));
  };

  const expandAllPanels = () => {
    setCollapsedPanels({
      groupInfo: false,
      namesConfig: false,
      logoConfig: false,
      faviconConfig: false,
      livePreview: false,
    });
  };

  const collapseAllPanels = () => {
    setCollapsedPanels({
      groupInfo: true,
      namesConfig: true,
      logoConfig: true,
      faviconConfig: true,
      livePreview: true,
    });
  };

  const totalPanelsCount = 5;
  const expandedPanelsCount =
    totalPanelsCount - Object.values(collapsedPanels).filter(Boolean).length;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Preview device mode: desktop | mobile
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [previewTheme, setPreviewTheme] = useState<"dark" | "light">("dark");

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const faviconFileInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch settings for currently selected group
  const fetchGroupBrand = async (groupName: string) => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/system-brand-settings?group=${encodeURIComponent(groupName)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tg_auth_token") || ""}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        const merged = {
          ...DEFAULT_BRAND_SETTING,
          ...data,
          group: groupName,
        };
        setFormData(merged);
        // Also update live preview favicon if admin is viewing or user is in this group
        if (isAdmin || userGroup === groupName) {
          applyFaviconToDocument(merged);
        }
      } else {
        setFormData({
          ...DEFAULT_BRAND_SETTING,
          group: groupName,
        });
      }
    } catch (err) {
      console.error("Failed to load group brand:", err);
      setFormData({
        ...DEFAULT_BRAND_SETTING,
        group: groupName,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load when selected group changes
  useEffect(() => {
    if (selectedGroup) {
      fetchGroupBrand(selectedGroup);
    }
  }, [selectedGroup]);

  // Load all existing groups for admin
  useEffect(() => {
    if (isAdmin) {
      fetch("/api/system-brand-settings?all=true", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tg_auth_token") || ""}`,
        },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && Array.isArray(data.groups)) {
            const set = new Set<string>(["默认分组", ...data.groups]);
            setGroupOptions(Array.from(set));
          }
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  // Real-time live update document title & favicon when formData changes in editor
  useEffect(() => {
    if (isAdmin || userGroup === formData.group) {
      document.title = formData.browserTitle || formData.systemName || "Telegram 营销自动化管理中心";
      applyFaviconToDocument(formData);
    }
  }, [
    formData.browserTitle,
    formData.systemName,
    formData.faviconType,
    formData.faviconIcon,
    formData.faviconUrl,
    formData.faviconBg,
    formData.faviconShape,
    formData.logoType,
    formData.logoImageUrl,
    formData.logoIcon,
  ]);

  // Handle Logo image file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("请选择有效的图片文件 (PNG, JPG, SVG, WebP)");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("上传图片不能超过 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) {
        setFormData((prev) => ({
          ...prev,
          logoType: "image",
          logoImageUrl: base64,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle Favicon image file upload
  const handleFaviconFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.name.endsWith(".ico")) {
      alert("请选择有效的图标文件 (ICO, PNG, SVG, WebP)");
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      alert("Favicon 图标大小不能超过 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) {
        setFormData((prev) => ({
          ...prev,
          faviconType: "custom",
          faviconUrl: base64,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle Save
  const handleSave = async () => {
    if (!formData.systemName.trim()) {
      alert("请输入系统程序主名称");
      return;
    }

    setSaving(true);
    setToastMessage(null);
    try {
      const res = await fetch("/api/system-brand-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("tg_auth_token") || ""}`,
        },
        body: JSON.stringify(formData),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "保存失败");
      }

      setToastMessage({
        type: "success",
        text: `分组 [${formData.group}] 的系统名称、Logo 与浏览器地址图标已成功保存并即时生效！`,
      });

      // Update app state immediately!
      if (isAdmin || userGroup === formData.group) {
        onBrandUpdated(json.brand);
        document.title = json.brand.browserTitle || json.brand.systemName;
        applyFaviconToDocument(json.brand);
      }
    } catch (err: any) {
      setToastMessage({
        type: "error",
        text: err.message || "保存配置发生异常，请检查网络",
      });
    } finally {
      setSaving(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Handle Reset to Default
  const handleReset = async () => {
    if (
      !confirm(
        `确定要将分组 [${formData.group}] 的系统名称、Logo 与浏览器图标恢复为初始默认设置吗？`
      )
    ) {
      return;
    }

    setResetting(true);
    setToastMessage(null);
    try {
      const res = await fetch("/api/system-brand-settings/reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("tg_auth_token") || ""}`,
        },
        body: JSON.stringify({ group: formData.group }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "重置失败");
      }

      setFormData(json.brand);
      setToastMessage({
        type: "success",
        text: `分组 [${formData.group}] 已恢复初始默认外观设置！`,
      });

      if (isAdmin || userGroup === formData.group) {
        onBrandUpdated(json.brand);
        document.title = json.brand.browserTitle || json.brand.systemName;
        applyFaviconToDocument(json.brand);
      }
    } catch (err: any) {
      setToastMessage({
        type: "error",
        text: err.message || "恢复默认发生异常",
      });
    } finally {
      setResetting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Apply Quick Style Preset
  const applyPresetTheme = (theme: (typeof PRESET_COLOR_THEMES)[0]) => {
    setFormData((prev) => ({
      ...prev,
      logoBg: theme.bg,
      logoBorder: theme.border,
      logoColor: theme.color,
    }));
  };

  // Live favicon preview URI for mock tab
  const currentFaviconPreviewUri = generateFaviconDataUri(formData);

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar-y p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Top Banner / Title Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400">
              <Sliders className="w-5 h-5" />
            </div>
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-white tracking-tight">
              系统设置面板
            </h1>
            <span className="px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-mono font-medium">
              品牌与外观定制
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-400">
            自定义系统程序主名称、Logo、浏览器地址图标 (Favicon)。每个分组独立设置，互不干扰。
          </p>
        </div>

        {/* Global Expand/Collapse Tool & Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 rounded-xl p-1 shadow-sm">
            <span className="text-[11px] text-slate-400 px-2 font-mono">
              已展开 {expandedPanelsCount}/{totalPanelsCount}
            </span>
            <button
              type="button"
              onClick={expandAllPanels}
              className="px-2.5 py-1 rounded-lg bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-medium transition flex items-center gap-1 cursor-pointer border border-slate-800"
              title="全部展开所有配置面板"
            >
              <ChevronsDown className="w-3.5 h-3.5 text-sky-400" />
              <span>全部展开</span>
            </button>
            <button
              type="button"
              onClick={collapseAllPanels}
              className="px-2.5 py-1 rounded-lg bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-medium transition flex items-center gap-1 cursor-pointer border border-slate-800"
              title="全部折叠所有配置面板"
            >
              <ChevronsUp className="w-3.5 h-3.5 text-slate-400" />
              <span>全部折叠</span>
            </button>
          </div>
        </div>
      </div>

      {/* Toast Alert */}
      {toastMessage && (
        <div
          className={`p-3.5 rounded-xl border flex items-center justify-between text-xs transition-all ${
            toastMessage.type === "success"
              ? "bg-emerald-950/40 border-emerald-500/30 text-emerald-300"
              : "bg-rose-950/40 border-rose-500/30 text-rose-300"
          }`}
        >
          <div className="flex items-center gap-2">
            {toastMessage.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span>{toastMessage.text}</span>
          </div>
          <button
            onClick={() => setToastMessage(null)}
            className="p-1 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ============================================================ */}
      {/* 面板 1：分组与权限信息面板 (Collapsible)                       */}
      {/* ============================================================ */}
      <CollapsiblePanel
        id="panel-group"
        title="分组隔离与数据归属面板"
        subtitle="当前编辑的目标分组，支持超级管理员快速切换不同分组独立调优"
        icon={Compass}
        iconColorClass="text-emerald-400"
        badge={
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-mono font-bold">
            当前: {selectedGroup}
          </span>
        }
        isCollapsed={collapsedPanels.groupInfo}
        onToggle={() => togglePanel("groupInfo")}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-slate-950/60 border border-slate-800">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-200">目标生效分组:</span>
              <span className="px-2.5 py-0.5 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 text-xs font-bold font-mono">
                {selectedGroup}
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              每个分组均享受完整独立的名号、Logo 与 Favicon 图标，互不影响、完全隔离。
            </p>
          </div>

          {/* Group selector for admin */}
          {isAdmin ? (
            <div className="flex items-center gap-2">
              {!isCustomGroupInput ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 font-medium">选择切换分组:</span>
                  <select
                    value={selectedGroup}
                    onChange={(e) => {
                      if (e.target.value === "__NEW__") {
                        setIsCustomGroupInput(true);
                      } else {
                        setSelectedGroup(e.target.value);
                      }
                    }}
                    className="bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg border border-slate-700 focus:outline-none focus:border-sky-500 font-medium cursor-pointer"
                  >
                    {groupOptions.map((g) => (
                      <option key={g} value={g}>
                        {g === "默认分组" ? "🌐 默认分组 (系统基准)" : `📁 ${g}`}
                      </option>
                    ))}
                    <option value="__NEW__">➕ 输入其他新分组...</option>
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    placeholder="输入新分组名称..."
                    value={customGroupName}
                    onChange={(e) => setCustomGroupName(e.target.value)}
                    className="bg-slate-900 text-white text-xs px-2.5 py-1.5 rounded-lg border border-slate-700 focus:outline-none focus:border-sky-500 w-36"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = customGroupName.trim();
                      if (trimmed) {
                        if (!groupOptions.includes(trimmed)) {
                          setGroupOptions([...groupOptions, trimmed]);
                        }
                        setSelectedGroup(trimmed);
                      }
                      setIsCustomGroupInput(false);
                      setCustomGroupName("");
                    }}
                    className="px-2.5 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold"
                  >
                    确认
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCustomGroupInput(false)}
                    className="p-1.5 text-slate-400 hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-400">
              您当前所属分组：
              <span className="text-white font-bold font-mono">{userGroup}</span>
              （系统已自动绑定）
            </div>
          )}
        </div>

        {/* Notice Info Box */}
        <div className="p-3.5 rounded-xl bg-indigo-950/20 border border-indigo-500/20 flex gap-2.5 items-start">
          <Info className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
          <div className="text-[11px] text-indigo-300/90 leading-relaxed space-y-1">
            <span className="font-bold text-indigo-200">独立隔离机制工作原理:</span>
            <p>
              修改当前面板保存后，仅对属于分组「
              <strong className="text-white font-mono">{formData.group}</strong>
              」的业务操作员或登录账号生效。其他分组登录时自动加载属于该组的独立专属名称与
              Logo。
            </p>
          </div>
        </div>
      </CollapsiblePanel>

      {/* ============================================================ */}
      {/* 面板 2：系统程序名称与文案配置面板 (Collapsible)               */}
      {/* ============================================================ */}
      <CollapsiblePanel
        id="panel-names"
        title="程序名称与文案配置面板"
        subtitle="自定义系统程序主名称、移动端小屏幕简称、浏览器网页标签页标题及宣传标语"
        icon={Type}
        iconColorClass="text-sky-400"
        badge={
          <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[10px] font-mono truncate max-w-[180px]">
            {formData.systemName || "默认主名称"}
          </span>
        }
        isCollapsed={collapsedPanels.namesConfig}
        onToggle={() => togglePanel("namesConfig")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {/* System Name */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
              <span>系统程序主名称 (桌面端常驻标题)</span>
              <span className="text-[10px] text-slate-500 font-mono">
                {formData.systemName.length}/60
              </span>
            </label>
            <input
              type="text"
              maxLength={60}
              value={formData.systemName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, systemName: e.target.value }))
              }
              placeholder="例如：Telegram 营销自动化管理中心"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 font-medium"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              常驻显示在电脑端顶部导航栏左侧。留空时默认采用 "Telegram 营销自动化管理中心"。
            </p>
          </div>

          {/* Mobile Name */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
              <span>移动端显示简称 (手机小屏幕适配)</span>
              <span className="text-[10px] text-slate-500 font-mono">
                {formData.mobileName.length}/30
              </span>
            </label>
            <input
              type="text"
              maxLength={30}
              value={formData.mobileName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, mobileName: e.target.value }))
              }
              placeholder="例如：Telegram 营销系统"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 font-medium"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              当手机端或窄屏展示时自动收缩为简称，避免顶部按钮挤压变形。
            </p>
          </div>

          {/* Browser Document Title */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
              <span>浏览器标签页标题 (Document Title)</span>
              <span className="text-[10px] text-slate-500 font-mono">
                {(formData.browserTitle || "").length}/60
              </span>
            </label>
            <input
              type="text"
              maxLength={60}
              value={formData.browserTitle || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, browserTitle: e.target.value }))
              }
              placeholder="例如：Telegram 营销自动化管理中心"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 font-medium"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              实时更新网页顶部浏览器 Tab 标签页上显示的标题文本。
            </p>
          </div>

          {/* Slogan */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
              <span>辅助宣传标语 / 副标题 (Slogan - 选填)</span>
              <span className="text-[10px] text-slate-500 font-mono">
                {(formData.slogan || "").length}/80
              </span>
            </label>
            <input
              type="text"
              maxLength={80}
              value={formData.slogan || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, slogan: e.target.value }))
              }
              placeholder="例如：多账号矩阵·自动化获客中枢"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 font-medium"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              在超宽屏电脑端展示在主标题下方的细微宣传副标题。
            </p>
          </div>
        </div>
      </CollapsiblePanel>

      {/* ============================================================ */}
      {/* 面板 3：系统 Logo 与外观风格面板 (Collapsible)                 */}
      {/* ============================================================ */}
      <CollapsiblePanel
        id="panel-logo"
        title="系统 Logo 图标与配色风格面板"
        subtitle="选择内置矢量精选图标库、上传本地高清图片 Logo、定制配色方案与圆角裁切"
        icon={Palette}
        iconColorClass="text-amber-400"
        badge={
          <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-mono">
            {formData.logoType === "preset"
              ? `内置: ${formData.logoIcon}`
              : "自定义图片 Logo"}
          </span>
        }
        isCollapsed={collapsedPanels.logoConfig}
        onToggle={() => togglePanel("logoConfig")}
        headerAction={
          <div className="flex items-center bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-[10px]">
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, logoType: "preset" }))
              }
              className={`px-2 py-1 rounded-md font-semibold flex items-center gap-1 transition cursor-pointer ${
                formData.logoType === "preset"
                  ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Sparkles className="w-3 h-3" />
              精选矢量
            </button>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, logoType: "image" }))
              }
              className={`px-2 py-1 rounded-md font-semibold flex items-center gap-1 transition cursor-pointer ${
                formData.logoType === "image"
                  ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <ImageIcon className="w-3 h-3" />
              自定义图片
            </button>
          </div>
        }
      >
        {/* Mode 1: Preset Icons */}
        {formData.logoType === "preset" ? (
          <div className="space-y-2.5">
            <label className="block text-xs font-semibold text-slate-300">
              选择内置矢量图标 ({PRESET_ICONS.length} 款)
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {PRESET_ICONS.map((item) => {
                const isSelected = formData.logoIcon === item.key;
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, logoIcon: item.key }))
                    }
                    className={`p-2.5 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer ${
                      isSelected
                        ? "bg-sky-500/20 border-sky-500 text-sky-300 shadow-md shadow-sky-500/10 scale-105"
                        : "bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-850 hover:text-slate-200 hover:border-slate-700"
                    }`}
                    title={item.label}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px] truncate max-w-full font-sans">
                      {item.label.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Mode 2: Custom Image */
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
                <span>图片网络外链 (Image URL)</span>
                {formData.logoImageUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, logoImageUrl: "" }))
                    }
                    className="text-[10px] text-rose-400 hover:underline flex items-center gap-0.5"
                  >
                    <X className="w-3 h-3" /> 清除已上传图片
                  </button>
                )}
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={
                    formData.logoImageUrl?.startsWith("data:")
                      ? "[已上传本地图片 Base64 格式]"
                      : formData.logoImageUrl || ""
                  }
                  disabled={formData.logoImageUrl?.startsWith("data:")}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      logoImageUrl: e.target.value.trim(),
                    }))
                  }
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3.5 py-2.5 pl-8 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono"
                />
                <LinkIcon className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-3" />
              </div>
            </div>

            {/* Upload from Local File */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-300">
                或者从本地选择图片上传 (PNG / SVG / JPG / WebP)
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-800 hover:border-sky-500/50 bg-slate-950/60 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition group"
              >
                <Upload className="w-6 h-6 text-slate-500 group-hover:text-sky-400 transition" />
                <span className="text-xs text-slate-400 group-hover:text-slate-200">
                  点击选择本地 Logo 图片或拖拽到此处
                </span>
                <span className="text-[10px] text-slate-600">
                  建议尺寸 64x64 ~ 256x256，支持透明背景 PNG / SVG (最大 2MB)
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        )}

        {/* Color Themes Selection */}
        <div className="space-y-2.5 pt-3 border-t border-slate-800">
          <label className="block text-xs font-semibold text-slate-300">
            Logo 底色与强调配色方案
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESET_COLOR_THEMES.map((theme) => {
              const isSelected =
                formData.logoBg === theme.bg &&
                formData.logoColor === theme.color;
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => applyPresetTheme(theme)}
                  className={`px-3 py-2 rounded-xl border flex items-center gap-2 text-xs transition-all cursor-pointer ${
                    isSelected
                      ? "bg-slate-850 border-sky-500 text-white shadow-sm ring-1 ring-sky-500/50"
                      : "bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-850 hover:text-slate-200"
                  }`}
                >
                  <div
                    className={`w-3.5 h-3.5 rounded-full ${theme.previewBg} shadow-sm shrink-0`}
                  />
                  <span className="truncate text-[11px] font-medium">
                    {theme.name.split(" ")[0]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Border Radius Style Selection */}
        <div className="space-y-2.5 pt-3 border-t border-slate-800">
          <label className="block text-xs font-semibold text-slate-300">
            Logo 外观圆角样式
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESET_RADIUS_OPTIONS.map((opt) => {
              const isSelected = formData.logoRadius === opt.class;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, logoRadius: opt.class }))
                  }
                  className={`px-3 py-2 rounded-xl border flex items-center justify-center text-xs transition-all cursor-pointer ${
                    isSelected
                      ? "bg-sky-500/10 border-sky-500 text-sky-400 font-bold"
                      : "bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <span className="text-[11px]">{opt.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </CollapsiblePanel>

      {/* ============================================================ */}
      {/* 面板 4：浏览器地址图标 (Favicon) 设置面板 (Collapsible) [NEW]   */}
      {/* ============================================================ */}
      <CollapsiblePanel
        id="panel-favicon"
        title="浏览器地址图标 (Favicon) 设置面板"
        subtitle="深度定制浏览器标签页 Tab 与网页书签地址图标，支持与系统Logo自动同步、精选矢量图标或自定义上传"
        icon={Globe}
        iconColorClass="text-cyan-400"
        badge={
          <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-[10px] font-mono">
            {formData.faviconType === "sync"
              ? "⚡ 与系统Logo同步"
              : formData.faviconType === "preset"
              ? `💎 专属图标: ${formData.faviconIcon || "Send"}`
              : "🖼️ 自定义 Favicon"}
          </span>
        }
        isCollapsed={collapsedPanels.faviconConfig}
        onToggle={() => togglePanel("faviconConfig")}
      >
        {/* Mode Selector Tabs */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-950 rounded-xl border border-slate-800">
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, faviconType: "sync" }))
            }
            className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition cursor-pointer ${
              formData.faviconType === "sync"
                ? "bg-cyan-500 text-slate-950 font-bold shadow-md shadow-cyan-500/20"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>与系统 Logo 智能同步 (推荐)</span>
          </button>

          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                faviconType: "preset",
                faviconIcon: prev.faviconIcon || "Send",
              }))
            }
            className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition cursor-pointer ${
              formData.faviconType === "preset"
                ? "bg-cyan-500 text-slate-950 font-bold shadow-md shadow-cyan-500/20"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Compass className="w-3.5 h-3.5" />
            <span>独立精选矢量 Favicon</span>
          </button>

          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, faviconType: "custom" }))
            }
            className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition cursor-pointer ${
              formData.faviconType === "custom"
                ? "bg-cyan-500 text-slate-950 font-bold shadow-md shadow-cyan-500/20"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <ImageIcon className="w-3.5 h-3.5" />
            <span>自定义上传图片 / 外链</span>
          </button>
        </div>

        {/* Mode 1 Content: Sync with Logo */}
        {formData.faviconType === "sync" && (
          <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="space-y-1 text-xs">
                <h3 className="font-semibold text-white">智能保持品牌一致性</h3>
                <p className="text-slate-400 leading-relaxed text-[11px]">
                  网页浏览器 Tab 标签图标将自动与当前分组的「系统 Logo」保持 100%
                  同步联动。当您切换或更新上方系统 Logo
                  时，浏览器地址图标自动更新，免去二次配置。
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-slate-800/80">
              <span className="text-xs text-slate-400">当前同步生成图标预览:</span>
              <div className="w-8 h-8 rounded-lg overflow-hidden border border-slate-700 bg-slate-900 flex items-center justify-center p-1">
                <img
                  src={currentFaviconPreviewUri}
                  alt="Favicon Sync Preview"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> 状态正常 · 实时联动中
              </span>
            </div>
          </div>
        )}

        {/* Mode 2 Content: Preset Vector Favicon */}
        {formData.faviconType === "preset" && (
          <div className="space-y-4 p-4 rounded-xl bg-slate-950/70 border border-slate-800">
            {/* Favicon Icon Picker */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-300">
                1. 选择专属 Favicon 图标 ({PRESET_FAVICON_ICONS.length} 款微缩优化矢量)
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {PRESET_FAVICON_ICONS.map((item) => {
                  const isSelected =
                    (formData.faviconIcon || "Send") === item.key;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          faviconIcon: item.key,
                        }))
                      }
                      className={`p-2.5 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer ${
                        isSelected
                          ? "bg-cyan-500/20 border-cyan-500 text-cyan-300 shadow-md shadow-cyan-500/10 scale-105"
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850 hover:text-slate-200"
                      }`}
                      title={item.label}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[10px] truncate max-w-full font-sans">
                        {item.label.split(" ")[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Favicon Background Color */}
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <label className="block text-xs font-semibold text-slate-300">
                2. Favicon 图标底色 (微缩图标高对比底色)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PRESET_FAVICON_COLORS.map((col) => {
                  const isSelected =
                    (formData.faviconBg || "#0284c7") === col.hex;
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          faviconBg: col.hex,
                        }))
                      }
                      className={`px-3 py-2 rounded-xl border flex items-center gap-2 text-xs transition-all cursor-pointer ${
                        isSelected
                          ? "bg-slate-850 border-cyan-500 text-white shadow-sm ring-1 ring-cyan-500/50 font-bold"
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850 hover:text-slate-200"
                      }`}
                    >
                      <div
                        className="w-3.5 h-3.5 rounded-full shadow-sm shrink-0"
                        style={{ backgroundColor: col.hex }}
                      />
                      <span className="truncate text-[11px]">{col.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Favicon Shape */}
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <label className="block text-xs font-semibold text-slate-300">
                3. Favicon 图标裁切形状
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {PRESET_FAVICON_SHAPES.map((shape) => {
                  const isSelected =
                    (formData.faviconShape || "rounded") === shape.id;
                  return (
                    <button
                      key={shape.id}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          faviconShape: shape.id as any,
                        }))
                      }
                      className={`px-3 py-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                        isSelected
                          ? "bg-cyan-500/10 border-cyan-500 text-cyan-300 font-semibold"
                          : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      <div className="text-xs font-bold text-white">
                        {shape.name}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {shape.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Mode 3 Content: Custom Upload Favicon */}
        {formData.faviconType === "custom" && (
          <div className="space-y-4 p-4 rounded-xl bg-slate-950/70 border border-slate-800">
            {/* Direct URL input */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
                <span>Favicon 图片外链 (URL)</span>
                {formData.faviconUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, faviconUrl: "" }))
                    }
                    className="text-[10px] text-rose-400 hover:underline flex items-center gap-0.5"
                  >
                    <X className="w-3 h-3" /> 清除图标
                  </button>
                )}
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={
                    formData.faviconUrl?.startsWith("data:")
                      ? "[已上传本地 Favicon 文件 Base64]"
                      : formData.faviconUrl || ""
                  }
                  disabled={formData.faviconUrl?.startsWith("data:")}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      faviconUrl: e.target.value.trim(),
                    }))
                  }
                  placeholder="https://example.com/favicon.ico"
                  className="w-full px-3.5 py-2.5 pl-8 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 font-mono"
                />
                <LinkIcon className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-3" />
              </div>
            </div>

            {/* Upload from Local File */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-300">
                或者上传本地专属 Favicon 文件 (.ico, .png, .svg, .webp)
              </label>
              <div
                onClick={() => faviconFileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-800 hover:border-cyan-500/50 bg-slate-900/60 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer transition group"
              >
                <Upload className="w-6 h-6 text-slate-500 group-hover:text-cyan-400 transition" />
                <span className="text-xs text-slate-400 group-hover:text-slate-200">
                  点击选择本地 Favicon 文件 (.ico / .png / .svg)
                </span>
                <span className="text-[10px] text-slate-600">
                  推荐尺寸 32x32 或 64x64 正方形，支持透明背景 (最大 1MB)
                </span>
                <input
                  ref={faviconFileInputRef}
                  type="file"
                  accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,image/webp"
                  onChange={handleFaviconFileUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        )}

        {/* Real-time Browser Tab Simulation Widget (所见即所得网页Tab模拟) */}
        <div className="space-y-2 pt-2 border-t border-slate-800">
          <label className="block text-xs font-semibold text-slate-300 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-cyan-400" />
              <span>真实浏览器标签页 (Browser Tab) 实时视觉模拟</span>
            </span>
            <span className="text-[10px] text-cyan-400 font-mono">
              实时动态映射中
            </span>
          </label>

          {/* Realistic Mock Chrome Tab Bar */}
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-3 overflow-hidden shadow-inner">
            <div className="flex items-center gap-2 pb-2.5 border-b border-slate-800/80">
              {/* Browser control dots */}
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              </div>
              <span className="text-[10px] text-slate-500 font-mono pl-1">
                Browser Address Bar & Tab Bar
              </span>
            </div>

            {/* Simulated Tabs */}
            <div className="flex items-center gap-2 pt-2.5">
              {/* Active Tab */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700/80 text-white text-xs max-w-xs shadow-md">
                {/* Simulated Favicon */}
                <div className="w-4 h-4 rounded overflow-hidden shrink-0 flex items-center justify-center bg-transparent">
                  <img
                    src={currentFaviconPreviewUri}
                    alt="Active Tab Favicon"
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* Title */}
                <span className="truncate font-medium text-[11px]">
                  {formData.browserTitle ||
                    formData.systemName ||
                    "Telegram 营销自动化管理中心"}
                </span>
                {/* Close Tab X */}
                <span className="text-slate-500 hover:text-slate-300 text-[10px] pl-1 cursor-pointer">
                  ✕
                </span>
              </div>

              {/* Inactive Tab Simulation */}
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-950 border border-transparent text-slate-500 text-[11px] max-w-[140px] opacity-60">
                <div className="w-3.5 h-3.5 rounded-full bg-sky-500/30 shrink-0" />
                <span className="truncate">Telegram Web</span>
              </div>
            </div>
          </div>
        </div>
      </CollapsiblePanel>

      {/* ============================================================ */}
      {/* 面板 5：实时全景多端渲染预览面板 (Collapsible)                 */}
      {/* ============================================================ */}
      <CollapsiblePanel
        id="panel-preview"
        title="实时全景多端渲染效果预览面板"
        subtitle="桌面端顶部导航栏、移动端抽屉栏及登录卡片的即时渲染预览反馈"
        icon={LayoutTemplate}
        iconColorClass="text-purple-400"
        badge={
          <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[10px] font-mono">
            {previewTheme === "dark" ? "暗黑模式" : "亮色模式"} ·{" "}
            {previewMode === "desktop" ? "桌面端" : "移动端"}
          </span>
        }
        isCollapsed={collapsedPanels.livePreview}
        onToggle={() => togglePanel("livePreview")}
        headerAction={
          <div className="flex items-center gap-2">
            {/* Dark / Light simulation */}
            <div className="flex items-center bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-[10px]">
              <button
                type="button"
                onClick={() => setPreviewTheme("dark")}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  previewTheme === "dark"
                    ? "bg-purple-500 text-white font-bold shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                暗黑
              </button>
              <button
                type="button"
                onClick={() => setPreviewTheme("light")}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  previewTheme === "light"
                    ? "bg-purple-500 text-white font-bold shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                亮色
              </button>
            </div>

            {/* Desktop / Mobile simulation */}
            <div className="flex items-center bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-[10px]">
              <button
                type="button"
                onClick={() => setPreviewMode("desktop")}
                className={`px-2 py-0.5 rounded-md font-semibold flex items-center gap-1 transition cursor-pointer ${
                  previewMode === "desktop"
                    ? "bg-purple-500 text-white font-bold shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Monitor className="w-3 h-3" />
                桌面
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("mobile")}
                className={`px-2 py-0.5 rounded-md font-semibold flex items-center gap-1 transition cursor-pointer ${
                  previewMode === "mobile"
                    ? "bg-purple-500 text-white font-bold shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Smartphone className="w-3 h-3" />
                移动
              </button>
            </div>
          </div>
        }
      >
        {/* Live Preview Bar */}
        <div
          className={`rounded-xl border transition-all duration-200 overflow-hidden ${
            previewTheme === "dark"
              ? "bg-slate-900 border-slate-800 text-white"
              : "bg-white border-slate-200 text-slate-900 shadow-inner"
          } p-3.5`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {/* Simulated Mobile Menu Icon */}
              <div
                className={`p-1.5 rounded-lg border text-xs ${
                  previewTheme === "dark"
                    ? "bg-slate-800 border-slate-700 text-sky-400"
                    : "bg-slate-100 border-slate-200 text-sky-600"
                }`}
              >
                ☰
              </div>

              {/* Logo Preview */}
              <div
                className={`p-1.5 ${formData.logoBg} ${formData.logoRadius} border ${formData.logoBorder} flex items-center justify-center overflow-hidden shrink-0 transition-all`}
                style={{ width: "32px", height: "32px" }}
              >
                {formData.logoType === "image" && formData.logoImageUrl ? (
                  <img
                    src={formData.logoImageUrl}
                    alt="Logo Preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <BrandIconRenderer
                    iconName={formData.logoIcon}
                    className={`w-4 h-4 ${formData.logoColor}`}
                  />
                )}
              </div>

              {/* Title Preview */}
              <div>
                <h3
                  className={`text-xs sm:text-sm font-display font-semibold tracking-tight ${
                    previewTheme === "dark" ? "text-white" : "text-slate-900"
                  }`}
                >
                  {previewMode === "desktop"
                    ? formData.systemName || "Telegram 营销自动化管理中心"
                    : formData.mobileName ||
                      formData.systemName ||
                      "Telegram 营销系统"}
                </h3>
                {formData.slogan && (
                  <p
                    className={`text-[9px] ${
                      previewTheme === "dark" ? "text-slate-500" : "text-slate-400"
                    }`}
                  >
                    {formData.slogan}
                  </p>
                )}
              </div>
            </div>

            {/* Right side mock buttons */}
            <div className="flex items-center gap-1.5 text-[10px] opacity-75">
              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                汇总
              </span>
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                🌙 暗黑
              </span>
              <span className="px-2 py-0.5 rounded bg-sky-500 text-slate-950 font-bold">
                ☀️ 亮色
              </span>
            </div>
          </div>
        </div>

        {/* Login Page Preview Card */}
        <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-slate-200">
              登录界面品牌卡片呈现预览:
            </span>
            <p className="text-[11px] text-slate-400">
              未登录用户进入系统时，将在登录窗口中央看到当前分组定制的名称与 Logo。
            </p>
          </div>

          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 shadow-md">
            <div
              className={`p-2 ${formData.logoBg} ${formData.logoRadius} border ${formData.logoBorder} flex items-center justify-center shrink-0`}
              style={{ width: "36px", height: "36px" }}
            >
              {formData.logoType === "image" && formData.logoImageUrl ? (
                <img
                  src={formData.logoImageUrl}
                  alt="Login Logo Preview"
                  className="w-full h-full object-contain"
                />
              ) : (
                <BrandIconRenderer
                  iconName={formData.logoIcon}
                  className={`w-4 h-4 ${formData.logoColor}`}
                />
              )}
            </div>
            <div>
              <div className="text-xs font-bold text-white tracking-tight">
                {formData.systemName || "Telegram 营销自动化管理中心"}
              </div>
              <div className="text-[10px] text-slate-400">
                {formData.slogan || "多账号矩阵·自动化获客中枢"}
              </div>
            </div>
          </div>
        </div>
      </CollapsiblePanel>

      {/* ============================================================ */}
      {/* 底部固定操作栏 (Sticky Action Control Bar)                    */}
      {/* ============================================================ */}
      <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xl sticky bottom-2 z-20">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>
            修改保存后将立即写入数据库，并对所有登录「
            <strong className="text-white font-mono">{formData.group}</strong>
            」的用户即时同步更新。
          </span>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={handleReset}
            disabled={resetting || saving}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition border border-slate-700 cursor-pointer flex items-center justify-center gap-1.5"
            title="将当前分组品牌外观恢复为系统默认"
          >
            <RotateCcw
              className={`w-3.5 h-3.5 ${resetting ? "animate-spin" : ""}`}
            />
            <span>恢复默认设置</span>
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || resetting}
            className="flex-1 sm:flex-none px-6 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold transition shadow-lg shadow-sky-600/20 cursor-pointer flex items-center justify-center gap-2 active:scale-98"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>正在保存并同步...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>保存并即时生效</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsView;
